import { Coordinates, HikingTrail, RouteType } from "../types";
import { getDistance } from "./geometryUtils";
import { MAP_LAYERS } from "../constants";
import i18n from "../i18n";

/**
 * Nearby tur- og friluftsruter from Kartverket's national route database (Turrutebasen)
 * via the GeoNorge WFS. Used for spots inside Norway; abroad we fall back to OSM/Overpass.
 *
 * Notes from the live service:
 *  - WFS 2.0.0, GML 3.2 only (no GeoJSON). Default CRS EPSG:4258 → axis order is lat,lon.
 *  - Route TYPE is the FeatureType itself (Fotrute / Skiløype / Sykkelrute / AnnenRute).
 *  - Routes are stored as many short segments linked by `rutenavn`/`rutenummer`, so we
 *    group segments per route and greedily stitch them into a single drawable line.
 */

const WFS_URL = "https://wfs.geonorge.no/skwms1/wfs.turogfriluftsruter";
const APP_NS = "http://skjema.geonorge.no/SOSI/produktspesifikasjon/TurOgFriluftsruter/20171210";
const GML_NS = "http://www.opengis.net/gml/3.2";
const WFS_NS = "http://www.opengis.net/wfs/2.0";

const TYPE_FEATURES: { feature: string; routeType: RouteType }[] = [
  { feature: "Fotrute", routeType: "foot" },
  { feature: "Skiløype", routeType: "ski" },
  { feature: "Sykkelrute", routeType: "bike" },
  { feature: "AnnenRute", routeType: "other" },
];

export let lastGeoNorgeRouteDebug = {
  lastUrl: "",
  lastError: null as any,
  featureCount: 0,
  timestamp: 0,
};

/** Read the text of the first child element matching a local name within `parent`. */
function localText(parent: Element, ns: string, local: string): string | undefined {
  const el = parent.getElementsByTagNameNS(ns, local)[0];
  const txt = el?.textContent?.trim();
  return txt && txt.length > 0 ? txt : undefined;
}

/** Parse a GML posList ("lat lon lat lon …", EPSG:4258) into Coordinates. */
function parsePosList(text: string): Coordinates[] {
  const nums = text.trim().split(/\s+/).map(Number);
  const coords: Coordinates[] = [];
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const lat = nums[i];
    const lon = nums[i + 1];
    if (!isNaN(lat) && !isNaN(lon)) coords.push({ latitude: lat, longitude: lon });
  }
  return coords;
}

/** All geometry segments (one per gml:posList) inside a single feature member. */
function segmentsFromMember(member: Element): Coordinates[][] {
  const posLists = member.getElementsByTagNameNS(GML_NS, "posList");
  const segments: Coordinates[][] = [];
  for (let i = 0; i < posLists.length; i++) {
    const seg = parsePosList(posLists[i].textContent || "");
    if (seg.length >= 2) segments.push(seg);
  }
  return segments;
}

const endpoints = (seg: Coordinates[]) => ({ a: seg[0], b: seg[seg.length - 1] });
const near = (p: Coordinates, q: Coordinates) => getDistance(p.latitude, p.longitude, q.latitude, q.longitude) < 25;

/** Greedily chain disjoint segments end-to-end into a single ordered polyline. */
function stitchSegments(segments: Coordinates[][]): Coordinates[] {
  if (segments.length === 0) return [];
  const remaining = segments.slice();
  let line = remaining.shift()!.slice();

  let progressed = true;
  while (remaining.length > 0 && progressed) {
    progressed = false;
    const head = line[0];
    const tail = line[line.length - 1];
    for (let i = 0; i < remaining.length; i++) {
      const { a, b } = endpoints(remaining[i]);
      if (near(tail, a)) { line = line.concat(remaining[i].slice(1)); remaining.splice(i, 1); progressed = true; break; }
      if (near(tail, b)) { line = line.concat(remaining[i].slice().reverse().slice(1)); remaining.splice(i, 1); progressed = true; break; }
      if (near(head, b)) { line = remaining[i].slice(0, -1).concat(line); remaining.splice(i, 1); progressed = true; break; }
      if (near(head, a)) { line = remaining[i].slice().reverse().slice(0, -1).concat(line); remaining.splice(i, 1); progressed = true; break; }
    }
  }
  return line;
}

function lineLength(line: Coordinates[]): number {
  let total = 0;
  for (let i = 0; i < line.length - 1; i++) {
    total += getDistance(line[i].latitude, line[i].longitude, line[i + 1].latitude, line[i + 1].longitude);
  }
  return total;
}

interface RouteGroup {
  key: string;
  name?: string;
  routeType: RouteType;
  segments: Coordinates[][];
  difficulty?: string;
  marked: boolean;
  ruteinfoUrl?: string;
}

function buildTypeUrl(feature: string, bbox: string): string {
  const params = new URLSearchParams({
    service: "WFS",
    version: "2.0.0",
    request: "GetFeature",
    typeNames: `app:${feature}`,
    count: "300",
    bbox,
  });
  return `${WFS_URL}?${params.toString()}`;
}

async function fetchType(feature: string, routeType: RouteType, bbox: string): Promise<RouteGroup[]> {
  const url = buildTypeUrl(feature, bbox);
  lastGeoNorgeRouteDebug.lastUrl = url;
  const res = await fetch(url, { headers: { Accept: "application/gml+xml" } });
  if (!res.ok) throw new Error(`WFS ${feature} HTTP ${res.status}`);
  const xml = await res.text();
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.getElementsByTagName("ows:Exception").length || doc.getElementsByTagName("ServiceException").length) {
    throw new Error(`WFS ${feature} exception`);
  }

  const members = doc.getElementsByTagNameNS(WFS_NS, "member");
  const groups = new Map<string, RouteGroup>();

  for (let i = 0; i < members.length; i++) {
    const member = members[i];
    const segments = segmentsFromMember(member);
    if (segments.length === 0) continue;

    const nameRaw = localText(member, APP_NS, "rutenavn");
    const name = nameRaw && !/^ukjent$/i.test(nameRaw) ? nameRaw : undefined;
    const rutenummer = localText(member, APP_NS, "rutenummer");
    // Group by name (or route number); unnamed segments group by rounded location so they don't all merge.
    const groupKey = name || rutenummer ||
      `loc:${segments[0][0].latitude.toFixed(2)},${segments[0][0].longitude.toFixed(2)}`;

    let group = groups.get(groupKey);
    if (!group) {
      group = {
        key: `geonorge-${routeType}-${groupKey}`,
        name,
        routeType,
        segments: [],
        difficulty: localText(member, APP_NS, "gradering"),
        marked: localText(member, APP_NS, "merking") === "JA",
      };
      groups.set(groupKey, group);
    }
    group.segments.push(...segments);
    if (!group.difficulty) group.difficulty = localText(member, APP_NS, "gradering");
    if (localText(member, APP_NS, "merking") === "JA") group.marked = true;
  }

  return Array.from(groups.values());
}

export const fetchGeoNorgeNearbyRoutes = async (coords: Coordinates): Promise<HikingTrail[]> => {
  if (!coords || isNaN(coords.latitude) || isNaN(coords.longitude)) return [];

  const radius = 2000; // metres
  const dLat = radius / 111320;
  const dLon = radius / (111320 * Math.cos((coords.latitude * Math.PI) / 180));
  const minLat = coords.latitude - dLat;
  const maxLat = coords.latitude + dLat;
  const minLon = coords.longitude - dLon;
  const maxLon = coords.longitude + dLon;
  const bbox = `${minLat},${minLon},${maxLat},${maxLon}`; // EPSG:4258 → lat,lon

  try {
    const groupsByType = await Promise.all(
      TYPE_FEATURES.map(({ feature, routeType }) =>
        fetchType(feature, routeType, bbox).catch(() => [] as RouteGroup[])
      )
    );
    const groups = groupsByType.flat();
    lastGeoNorgeRouteDebug.featureCount = groups.length;
    lastGeoNorgeRouteDebug.timestamp = Date.now();
    if (groups.length === 0) return [];

    const trails: HikingTrail[] = groups.map((g) => {
      const geometry = stitchSegments(g.segments);
      const totalLength = lineLength(geometry);
      const distStart = geometry.length
        ? Math.min(...geometry.map((p) => getDistance(coords.latitude, coords.longitude, p.latitude, p.longitude)))
        : Number.POSITIVE_INFINITY;
      const minutes = Math.round((totalLength / 1000 / 3.5) * 60);
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const estimatedTime = hrs > 0 ? `${hrs}t ${mins > 0 ? `${mins}min` : ""}` : `${mins}min`;

      return {
        id: g.key,
        name: g.name || i18n.t(`route.type.${g.routeType}`, defaultTypeName(g.routeType)),
        type: i18n.t(`route.type.${g.routeType}`, defaultTypeName(g.routeType)),
        routeType: g.routeType,
        marked: g.marked,
        distanceToStart: distStart,
        geometry,
        totalLength,
        estimatedTime,
        difficulty: g.difficulty,
      };
    });

    return trails
      .filter((t) => t.geometry.length >= 2 && (t.totalLength || 0) >= 300)
      .sort((a, b) => a.distanceToStart - b.distanceToStart)
      .slice(0, 20);
  } catch (error: any) {
    lastGeoNorgeRouteDebug.lastError = error?.message;
    console.debug(`[geonorgeRoutesService] WFS query failed: ${error?.message}`);
    return [];
  }
};

function defaultTypeName(rt: RouteType): string {
  switch (rt) {
    case "foot": return "Fottur";
    case "ski": return "Skiløype";
    case "bike": return "Sykkelrute";
    default: return "Tur";
  }
}

// --- Click-to-identify via WMS GetFeatureInfo ---

/** Attributes for one route hit by a map click on the hiking WMS layers. */
export interface TrailClickInfo {
  routeType: RouteType;
  name?: string;
  number?: string;
  maintainer?: string;
  /** Human-readable difficulty from Kartverket, e.g. "Enkel (grønn)". */
  grading?: string;
  /** Human-readable marking status, e.g. "Merket". */
  marking?: string;
  specialType?: string;
}

const WMS_TYPE_FEATURES: { feature: string; routeType: RouteType }[] = [
  { feature: "Fotrute", routeType: "foot" },
  { feature: "Skiloype", routeType: "ski" },
  { feature: "Sykkelrute", routeType: "bike" },
  { feature: "AnnenRute", routeType: "other" },
];

const featureText = (el: Element, tag: string): string | undefined => {
  const txt = el.getElementsByTagName(tag)[0]?.textContent?.trim();
  return txt && txt.length > 0 ? txt : undefined;
};

/**
 * Identify routes at a clicked map point using WMS GetFeatureInfo against the same
 * Kartverket service that renders the hiking tile layers. Only the currently visible
 * route types are queried. `metersPerPixel` controls the virtual view resolution:
 * the server's hit tolerance is ~3 px, so passing displayResolution*4 gives a
 * finger-friendly ~12 display-pixel tap target.
 */
export const fetchTrailInfoAtPoint = async (
  coords: Coordinates,
  activeTypes: RouteType[],
  metersPerPixel: number
): Promise<TrailClickInfo[]> => {
  const features = WMS_TYPE_FEATURES.filter((f) => activeTypes.includes(f.routeType));
  if (features.length === 0) return [];

  // Virtual 101x101 px view centred on the click point (I=J=50 is the exact click pixel).
  const SIZE = 101;
  const half = (SIZE - 1) / 2;
  const dLat = (metersPerPixel * half) / 111320;
  const dLon = (metersPerPixel * half) / (111320 * Math.cos((coords.latitude * Math.PI) / 180));
  const layerList = features.map((f) => f.feature).join(",");

  const params = new URLSearchParams({
    SERVICE: "WMS",
    VERSION: "1.3.0",
    REQUEST: "GetFeatureInfo",
    LAYERS: layerList,
    QUERY_LAYERS: layerList,
    STYLES: "",
    CRS: "EPSG:4326", // WMS 1.3.0 → axis order lat,lon
    BBOX: `${coords.latitude - dLat},${coords.longitude - dLon},${coords.latitude + dLat},${coords.longitude + dLon}`,
    WIDTH: String(SIZE),
    HEIGHT: String(SIZE),
    I: String(half),
    J: String(half),
    INFO_FORMAT: "application/vnd.ogc.gml",
    FEATURE_COUNT: "10",
  });

  const res = await fetch(MAP_LAYERS.GEONORGE_HIKING_WMS + params.toString());
  if (!res.ok) throw new Error(`GetFeatureInfo HTTP ${res.status}`);
  const xml = await res.text();
  const doc = new DOMParser().parseFromString(xml, "application/xml");

  const results: TrailClickInfo[] = [];
  const seen = new Set<string>();
  for (const { feature, routeType } of features) {
    const members = doc.getElementsByTagName(`${feature}_feature`);
    for (let i = 0; i < members.length; i++) {
      const m = members[i];
      const name = featureText(m, "rutenavn");
      const number = featureText(m, "rutenummer");
      const key = `${routeType}|${name || number || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      results.push({
        routeType,
        name: name && !/^ukjent$/i.test(name) ? name : undefined,
        number,
        maintainer: featureText(m, "vedlikeholdsansvarlig"),
        grading: featureText(m, "gradering_d"),
        marking: featureText(m, "merking_d"),
        specialType: featureText(m, "spesialrutetype_d"),
      });
    }
  }
  return results;
};
